import {
  Button,
  Center,
  Flex,
  Heading,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";

import appInsights from "@/utils/appInsights";
import { Select } from "chakra-react-select";
import NextLink from "next/link";
import { useRouter } from "next/router";
import { ReactElement, useEffect, useRef, useState } from "react";
import { BiImport } from "react-icons/bi";
import { IoIosCloseCircle } from "react-icons/io";
import {
  AddButton,
  AuthenticatedLayout,
  LoadingSpinner,
} from "../../components";
import RecipeCardV2 from "../../components/Cards/RecipeCardV2";
import FeaturedRecipesCheckbox from "../../components/Checkbox/FeaturedRecipesCheckbox";
import { OptionType } from "../../components/Forms/RecipeForm/RecipeIngredientForm";
import ImportFromInstagramModal from "../../components/Modals/ImportFromInstagramModal";
import ReusableGrid from "../../components/ReusableGrid/ReusableGrid";
import { sessionStorageKeys } from "../../config";
import { firebaseLogScreenView } from "../../config/firebase";
import { useDebounce, useGetRecipes, useScrollToItem } from "../../hooks";
import useGetUser from "../../hooks/users/useGetUser";
import { getSortByOptionFromSessionStorage, sortOptions } from "../../utils";
import additionalSortOptions from "../../utils/additionalSortOptions";

// const customStyles: StylesConfig = {
//   control: (provided: Record<string, unknown>, state: any) => ({
//     ...provided,
//     border: "1px solid #ff8b67",
//     //  boxShadow: state.isFocused ? "0px 0px 6px #ff8b67" : "none",
//     //  "&:hover": {
//     //    border: "1px solid #ff8b67",
//     //    boxShadow: "0px 0px 6px #ff8b67"
//     //  }
//   }),
// };

const Recipes = () => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedOption, setSelectedOption] = useState<OptionType>(
    getSortByOptionFromSessionStorage(sessionStorageKeys.recipesSortBy) ||
      sortOptions[0]
  );

  const debouncedValue = useDebounce(searchValue, 500);
  const inputRef = useRef<HTMLInputElement>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const router = useRouter();

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetRecipes(debouncedValue, selectedOption.value);

  const { isLoading: isGetUserLoading, data: userData } = useGetUser(true);

  const bgColor = useColorModeValue("gray.50", "gray.900");

  try {
    if (process.env.NODE_ENV == "production" && userData) {
      appInsights.trackEvent({
        name: "User Interaction Event",
        properties: {
          message: "User Navigated to recipe page",
          userId: userData?.id,
          userEmail: userData?.email,
        },
      });
    }
  } catch (error) {}

  useEffect(() => {
    const redirectSlug = localStorage.getItem("redirectSlug");

    appInsights.trackEvent({
      name: "Recipes Page - uef",
      properties: { redirectSlug, message: "Recipes Page - uef" },
    });

    if (redirectSlug) {
      appInsights.trackEvent({
        name: "Recipes Page - uef - has slug, attempting to push",
        properties: { redirectSlug, message: "Recipes Page - uef" },
      });
      router.push(redirectSlug);

      appInsights.trackEvent({
        name: "Recipes Page - uef - has slug, Completed Push",
        properties: { redirectSlug, message: "Recipes Page - uef" },
      });
    }
  }, []);

  const isDataLoading = isLoading || isFetching;
  const hasRecipes = data?.pages?.[0].length && !isLoading && !isFetching;

  // if (isLoading || isFetching) {
  //   return <LoadingSpinner />;
  // }

  firebaseLogScreenView("ViewAllRecipes", "ViewAllRecipesPage");

  useEffect(() => {
    if (debouncedValue) {
      inputRef.current?.focus();
    }
  }, [isFetching, debouncedValue]);

  useScrollToItem({
    enabled: !!hasRecipes,
    storageKey: sessionStorageKeys.recipeIdMarker,
  });

  //TODO: There's some weirdness where it will show the no items ilustration on reload. investigate.
  //TODO: Separate this.

  const NoItemsDisplay = () => {
    const noRecipesCreatedMessage =
      "It looks like you don't have any recipes yet!";
    const noRecipesFoundMessage = "No recipes found that match your search.";

    const noRecipesCreatedActionMessage =
      "Click on the create recipe button to get started";
    const noRecipesFoundActionMessage = "Please adjust your search term";

    return (
      <Flex mt={"10%"} mr="10px">
        <Center w="100%">
          <Flex direction="column" gap="5px">
            <Heading size="md">
              {debouncedValue && !isDataLoading
                ? noRecipesFoundMessage
                : noRecipesCreatedMessage}
            </Heading>
            <Heading size={"md"}>
              {debouncedValue
                ? noRecipesFoundActionMessage
                : noRecipesCreatedActionMessage}
            </Heading>

            <Image
              //   h="250px"
              boxSize={"sm"}
              src="/bbq_illustration.svg"
              alt="Create a Recipe"
              objectFit={"contain"}
            />
          </Flex>
        </Center>
      </Flex>
    );
  };

  return (
    // <Flex gap="25px" wrap="wrap">
    //TODO:If grid breaks -  Added a max height.

    <Flex direction="column" h="100%">
      <Flex
        gap="10px"
        mb="10px"
        mr={"10px"}
        pos="sticky"
        top="0px"
        zIndex={"500"}
        bg={bgColor}
      >
        <ImportFromInstagramModal
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onClose}
          onCancel={onClose}
          onSubmit={onClose}
        />
        <InputGroup>
          <Input
            m="5px"
            focusBorderColor="brand.500"
            placeholder="Search Recipes"
            bg={"white"}
            size="lg"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <InputRightElement h={"full"}>
            {searchValue && (
              <IconButton
                _hover={{ cursor: "pointer" }}
                aria-label="Clear Field"
                size="xl"
                icon={<IoIosCloseCircle size={"24px"} />}
                onClick={() => setSearchValue("")}
                variant="unstyled"
              />
            )}
          </InputRightElement>
        </InputGroup>
        <NextLink href="/recipes/new" passHref>
          <AddButton isDisabled={isDataLoading} />
        </NextLink>
        {userData?.isBetaTester && (
          <Button
            mt="5px"
            isDisabled={isDataLoading}
            leftIcon={<BiImport color="purple" size="42px" />}
            onClick={() => onOpen()}
            variant="unstyled"
          />
        )}
      </Flex>
      <Flex mb="10px" mr={"10px"} justifyContent={"start"} gap="5%">
        <div style={{ width: "200px", marginLeft: 5 }}>
          <Select
            useBasicStyles
            chakraStyles={{
              container: (provided) => ({
                ...provided,
                backgroundColor: "white",
              }),
              option: (provided, { isSelected }) => ({
                ...provided,

                // ...(isSelected && {
                //   backgroundColor: "white",
                // }),
              }),
            }}
            focusBorderColor="brand.500"
            selectedOptionColorScheme="brand"
            value={selectedOption}
            colorScheme="brand.500"
            name={"sort-by-select"}
            options={[...sortOptions, ...additionalSortOptions]}
            onChange={(option) => {
              option?.value &&
                localStorage.setItem(
                  sessionStorageKeys.recipesSortBy,
                  option?.value
                );
              setSelectedOption(option as OptionType);
            }}
          />
        </div>
        <Flex gap="5px">
          <FeaturedRecipesCheckbox />
        </Flex>
      </Flex>
      {/*TODO: Investigate the styling of these cards. We might want to increase the image size / card size on bigger screens.*/}
      {isDataLoading && !isFetchingNextPage ? (
        <Center h="100%" w="100%">
          <LoadingSpinner />
        </Center>
      ) : hasRecipes || isFetchingNextPage ? (
        <ReusableGrid
          dataLength={data?.pages.flat().length}
          fetchNextPage={fetchNextPage}
          hasNextPage={!!hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
        >
          {data?.pages?.map((page) =>
            page.map((d) => (
              <RecipeCardV2
                isCreatedByCurrentUser={userData?.id == d.createdByUserId}
                recipe={d}
                key={d.id}
              />
            ))
          )}
        </ReusableGrid>
      ) : (
        <NoItemsDisplay />
      )}
    </Flex>
  );
};

Recipes.getLayout = function getLayout(page: ReactElement) {
  return (
    <AuthenticatedLayout border={false} overFlow={true} pageTitle={"Recipes"}>
      {page}
    </AuthenticatedLayout>
  );
};

export default Recipes;
